import React, { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useHistory } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'

import {
  getAgentUsageStatistics,
  AgentUsageStatisticsRow,
} from '@/services/OrganisationDashboardService'
import { useGlobalContext } from '@/context/GlobalContext'
import { formatPercent } from '@/utils/dashboardUtils'
import ExportReportShared from '@/components/UI/ExportReportShared/ExportReportShared'
import { getAgentFromStorage, selectAgent } from '@/utils/appUtils'
import { updateSelectedAgent } from '@/store/agents/actions'

interface AgentEngagementProps {
  filtersData: any
}

export default function AgentEngagement({
  filtersData,
}: AgentEngagementProps): ReactElement {
  const { t } = useTranslation('translation')
  const { currentOrganisation, setSelectedAgentName } = useGlobalContext()
  const history = useHistory()
  const dispatch = useDispatch()
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [loadingAgentId, setLoadingAgentId] = useState<string | null>(null)
  const agentId = getAgentFromStorage()

  const handleAgentClick = async (agentId: string, agentName: string) => {
    setLoadingAgentId(agentId)

    try {
      await selectAgent({
        agentId,
        agentName,
        dispatch,
        history,
        setSelectedAgentName,
        updateSelectedAgent,
      })
    } catch (error: any) {
      // Error is already handled by the utility function (toast notification)
    } finally {
      setLoadingAgentId(null)
    }
  }

  const columns: GridColDef<AgentUsageStatisticsRow>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('Agent Name'),
        width: 200,
        renderCell: params => {
          const isLoading = loadingAgentId === params.row._id
          return (
            <Typography
              variant="body3Link"
              fontWeight={500}
              onClick={() =>
                !isLoading && handleAgentClick(params.row._id, params.value)
              }
              sx={{
                cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading ? t('Selecting...') : params.value}
            </Typography>
          )
        },
      },
      {
        field: 'quiz_submission_count',
        headerName: t('Quizzes Submitted'),
        flex: 1,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'roleplay_submission_count',
        headerName: t('Role Plays Completed'),
        flex: 1,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'question_count',
        headerName: t('Questions Asked'),
        flex: 1,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'answered_question_count',
        headerName: t('Questions Answered'),
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: params => {
          const answered = Number(
            params.row.answered_question_count ?? params.value ?? 0,
          )
          const total = Number(params.row.question_count ?? 0)
          return `${answered} (${formatPercent(answered, total, 1)})`
        },
      },
      {
        field: 'active_user_count',
        headerName: t('Active Learners'),
        flex: 1,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'returning_user_count',
        headerName: t('% Returning Learners'),
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: params => {
          const returning = Number(
            params.row.returning_user_count ?? params.value ?? 0,
          )
          const active = Number(params.row.active_user_count ?? 0)
          return formatPercent(returning, active, 1)
        },
      },
    ],
    [t, loadingAgentId],
  )

  const { data, isLoading } = useQuery({
    queryKey: [
      'org-agent-usage',
      filtersData,
      paginationModel.page,
      paginationModel.pageSize,
      agentId,
    ],
    queryFn: () =>
      getAgentUsageStatistics({
        ...filtersData,
        page: paginationModel.page + 1, // API is 1-indexed, DataGrid is 0-indexed
        page_size: paginationModel.pageSize,
      }),
    enabled:
      !!currentOrganisation?._id &&
      !!(filtersData.filter_from_date && filtersData.filter_to_date),
  })

  // Use ref to maintain row count during loading to prevent DataGrid flickering
  const rowCountRef = React.useRef(data?.pagination.total_items || 0)

  const rowCount = React.useMemo(() => {
    if (data?.pagination.total_items !== undefined) {
      rowCountRef.current = data.pagination.total_items
    }
    return rowCountRef.current
  }, [data?.pagination.total_items])

  return (
    <div className="tw-border tw-rounded-2xl tw-shadow-md tw-p-8">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <Typography variant="subtitle2" component="h2">
          {t('Agent Engagement')}
        </Typography>
        <ExportReportShared
          name="org-agent-usage"
          filtersData={filtersData}
          isAsync={true}
        />
      </div>
      <Box sx={{ height: 550, width: '100%' }}>
        <DataGrid
          getRowId={row => row._id}
          rows={data?.rows || []}
          columns={columns}
          loading={isLoading}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
          disableColumnSorting
          sx={{
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f9fafb',
              fontSize: '0.875rem',
              fontWeight: 600,
            },
          }}
        />
      </Box>
    </div>
  )
}
